import { Box, Typography } from '@mui/material';
import React, { ReactElement } from 'react';
import { LabelPill } from '../LabelPill';
import { Switch } from '../Switch';

export interface LabeledSwitchRowProps {
  label: string;
  subLabel?: string;
  /** Label shown in the pill when `checked` is true. */
  activeLabel: string;
  /** Label shown in the pill when `checked` is false. */
  inactiveLabel: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}

/**
 * Row UI used in the event create/edit form: a left-aligned label (with
 * optional subtitle) and a right-aligned {@link LabelPill} + navy toggle
 * switch. Mirrors Figma node 4351:6429 / 4318:5034.
 */
export const LabeledSwitchRow = ({
  label,
  subLabel,
  activeLabel,
  inactiveLabel,
  checked,
  onChange,
  disabled = false,
}: LabeledSwitchRowProps): ReactElement => {
  const pillLabel = checked ? activeLabel : inactiveLabel;

  const handleToggle = () => {
    if (disabled) return;
    onChange(!checked);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '16px',
            fontWeight: 500,
            color: '#383838',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        {subLabel && (
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '18px',
              color: '#383838',
            }}
          >
            {subLabel}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0,
        }}
      >
        <LabelPill label={pillLabel} active={checked} />
        <Switch on={checked} onToggle={handleToggle} disabled={disabled} />
      </Box>
    </Box>
  );
};
